import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  getMeters,
  getMeter,
  createMeter,
  updateMeter,
  deleteMeter,
  getKPIs,
  getKPI,
  createKPI,
  deleteKPI,
  getGraphData
} from '../services/meterApi'; // Adjust path as needed

/* ==================== Meters Hooks ==================== */

/**
 * Hook to fetch all meters
 */
export const useMeters = () => {
  return useQuery('meters', getMeters, {
    staleTime: 1000, // 5 minutes
    cacheTime: 1000,
  });
};

/**
 * Hook to fetch single meter
 */
export const useMeter = (meterId) => {
  return useQuery(
    ['meter', meterId],
    () => getMeter(meterId),
    {
      enabled: !!meterId,
      staleTime: 1000,
      cacheTime: 1000,
    }
  );
};

/**
 * Hook to create meter
 */
export const useCreateMeter = () => {
  const queryClient = useQueryClient();
  
  return useMutation(createMeter, {
    onSuccess: () => {
      queryClient.invalidateQueries('meters');
    },
  });
};

/**
 * Hook to update meter
 */
export const useUpdateMeter = (meterId) => {
  const queryClient = useQueryClient();
  
  return useMutation(
    (data) => updateMeter(meterId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('meters');
        queryClient.invalidateQueries(['meter', meterId]);
      },
    }
  );
};

/**
 * Hook to delete meter
 */
export const useDeleteMeter = () => {
  const queryClient = useQueryClient();
  
  return useMutation(deleteMeter, {
    onSuccess: () => {
      queryClient.invalidateQueries('meters');
    },
  });
};

/* ==================== KPI Hooks ==================== */

/**
 * Hook to fetch all KPIs
 * @param {object} params - { kpiType: 'INTERNAL' | 'EXTERNAL' }
 */
export const useKPIs = (params = {}) => {
  return useQuery(
    ['kpis', params],
    () => getKPIs(params),
    {
      staleTime: 1000,
      cacheTime: 1000,
    }
  );
};

/**
 * Hook to fetch single KPI
 */
export const useKPI = (kpiId) => {
  return useQuery(
    ['kpi', kpiId],
    () => getKPI(kpiId),
    {
      enabled: !!kpiId,
      staleTime: 1000,
    }
  );
};

/**
 * Hook to create KPI
 */
export const useCreateKPI = () => {
  const queryClient = useQueryClient();
  
  return useMutation(createKPI, {
    onSuccess: () => {
      queryClient.invalidateQueries('kpis');
    },
  });
};

/**
 * Hook to delete KPI
 */
export const useDeleteKPI = () => {
  const queryClient = useQueryClient();
  
  return useMutation(deleteKPI, {
    onSuccess: () => {
      queryClient.invalidateQueries('kpis');
    },
  });
};

/* ==================== Graph Data Hooks ==================== */

/**
 * Hook to fetch graph data
 * Use this for all meter visualizations
 */
export const useGraphData = (graphConfig, options = {}) => {
  return useQuery(
    ['graphData', graphConfig],
    () => getGraphData(graphConfig),
    {
      enabled: !!graphConfig && options.enabled !== false,
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 5,
      ...options
    }
  );
};

/**
 * Hook for manual graph data fetching
 * Useful when you need to trigger graph fetch manually
 */
export const useGraphDataMutation = () => {
  return useMutation(getGraphData);
};